import AnimatedCollapsingBox from '@/components/AnimatedHeightBox';
import RadioButtonIcon from '@/components/RadioButtonIcon';
import {colorStyleMap} from '@/utilities/color-style-map';
import classNames from 'classnames';
import {ComponentChildren} from 'preact';

/**
 * A box for displaying a selectable item.
 * It can take children to display when the item is selected.
 * The `label` prop is the label to display in the box.
 * If the `centerLabel` prop is `true`, the label will be positioned in the center of the box.
 * This may cause overlap with the checkbox, so it is recommended to use this prop only when the label is short.
 * The `color` prop can be used to change the color of the checkbox. See the colors supported by [colorStyleMap].
 */
export default function SelectorItemRadioBox({
  badgeLabel,
  centerLabel = false,
  children,
  color = 'breeze-blue',
  footer,
  isSelected,
  label,
  onClick,
  showBackgroundColor = false,
  dataTestid,
}: SelectorItemBoxProps) {
  const colorStyle =
    colorStyleMap.get(color) ?? colorStyleMap.get('breeze-blue')!;

  return (
    <div
      className={classNames('fast-transition h-max rounded-2xl border', {
        'border border-deep-ocean-blue/70': !isSelected,
        [`border-2 ${colorStyle.border}`]: isSelected,
      })}
    >
      <div
        className={classNames('fast-transition absolute inset-0 -z-10', {
          [colorStyle.background]: showBackgroundColor,
          'opacity-10': isSelected,
          'opacity-0': !isSelected,
        })}
      ></div>
      {badgeLabel && (
        <span
          className={classNames(
            'fast-transition block w-full cursor-pointer rounded-t-2xl border-b px-6 py-1 text-center text-sm font-semibold',
            {
              [`${colorStyle.border} ${colorStyle.backgroundOpacity12}`]:
                isSelected,
              'border-deep-ocean-blue/70 bg-deep-ocean-blue/[.12]': !isSelected,
            }
          )}
          onClick={onClick}
        >
          {badgeLabel}
        </span>
      )}
      <button
        data-testid={dataTestid}
        className={classNames(
          'grid-cols-max-grow relative z-10 grid w-full items-center justify-start gap-x-4 p-3 font-medium tracking-normal',
          {
            'md:grid-cols-1': centerLabel,
          }
        )}
        onClick={onClick}
      >
        <div
          className={classNames({
            'md:absolute md:left-4 md:top-1/2 md:-translate-y-1/2': centerLabel,
          })}
        >
          <RadioButtonIcon selected={isSelected} color={color} />
        </div>
        {label}
      </button>

      <AnimatedCollapsingBox
        isCollapsed={!isSelected}
        className="duration-500 ease-in-out"
      >
        {children && <div className="relative px-3 pb-3">{children}</div>}
      </AnimatedCollapsingBox>
      {footer}
    </div>
  );
}

export type SelectorItemBoxProps = {
  badgeLabel?: ComponentChildren;
  centerLabel?: boolean;
  children?: ComponentChildren;
  color?: string;
  footer?: ComponentChildren;
  isSelected: boolean;
  label: ComponentChildren;
  onClick?: () => void;
  showBackgroundColor?: boolean;
  dataTestid?: string;
};
